import { ExternalLink, MoreHorizontal, Pencil, Plus, Search, Trash2, UserPlus, Users } from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { PageHeader, TeamFormModal, type TeamFormValues, useConfirm, useToast } from "@/components/shared";
import { useApp } from "@/context/AppContext";
import { useAuth } from "@/context/AuthContext";
import { getApiErrorMessage } from "@/lib";
import { teamService } from "@/lib/services";

export function Teams() {
  const { teams, refreshTeams } = useApp();
  const { user } = useAuth();
  const { showToast } = useToast();
  const { confirm } = useConfirm();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedTeam, setSelectedTeam] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  const isPlatformAdmin = user?.isPlatformAdmin ?? false;

  const handleDeleteTeam = async (teamUuid: string) => {
    setSelectedTeam(null);
    const confirmed = await confirm({
      title: "Delete Team",
      message: "Are you sure you want to delete this team? This action cannot be undone.",
      confirmText: "Delete",
      variant: "danger",
    });
    if (!confirmed) return;

    try {
      await teamService.delete(teamUuid);
      showToast("Team deleted successfully", "success");
      await refreshTeams();
    } catch (err) {
      console.error("Failed to delete team:", err);
      showToast("Failed to delete team", "error");
    }
  };

  const filteredTeams = teams.filter(
    (team) =>
      team.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      team.description?.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  const handleCreateTeam = async (values: TeamFormValues) => {
    try {
      setCreating(true);
      await teamService.create({
        name: values.name,
        description: values.description || undefined,
      });
      showToast("Team created successfully", "success");
      await refreshTeams();
      setShowCreateModal(false);
    } catch (err) {
      console.error("Failed to create team:", err);
      showToast(getApiErrorMessage(err, "Failed to create team"), "error");
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="flex-1 flex flex-col min-h-0">
      {/* Header */}
      <PageHeader
        icon={Users}
        title="Teams"
        subtitle="Your teams and shared workspaces"
        actions={
          isPlatformAdmin ? (
            <button
              onClick={() => setShowCreateModal(true)}
              className="flex items-center gap-2 px-4 py-2.5 bg-brand-gradient text-white rounded-lg font-medium hover:opacity-90 transition-opacity"
            >
              <Plus className="h-4 w-4" />
              Create Team
            </button>
          ) : undefined
        }
      >
        {/* Search */}
        <div className="mt-4 relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-content-quaternary" />
          <input
            type="text"
            placeholder="Search teams..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 bg-surface-primary border border-border-secondary rounded-lg text-content-primary placeholder:text-content-quaternary focus:outline-hidden focus:ring-2 focus:ring-brand-primary/50 focus:border-brand-primary"
          />
        </div>
      </PageHeader>

      {/* Content */}
      <div className="flex-1 overflow-auto p-6">
        {filteredTeams.length > 0 ? (
          <div className="grid grid-cols-[repeat(auto-fill,minmax(18rem,1fr))] gap-4">
            {filteredTeams.map((team) => (
              <div
                key={team.uuid}
                role="button" // NOSONAR — contains nested interactive elements
                tabIndex={0}
                onClick={() => navigate(`/teams/${team.uuid}`)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    navigate(`/teams/${team.uuid}`);
                  }
                }}
                className="bg-surface-primary border border-border-primary rounded-xl p-5 hover:border-border-secondary transition-colors group cursor-pointer"
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-start gap-4 min-w-0 flex-1">
                    <div className="h-12 w-12 rounded-xl bg-linear-to-br from-brand-primary/20 to-brand-secondary/20 flex items-center justify-center shrink-0">
                      <Users className="h-6 w-6 text-brand-primary" />
                    </div>
                    <div className="min-w-0">
                      <h3 className="font-medium text-content-primary truncate group-hover:text-brand-primary transition-colors">
                        {team.name}
                      </h3>
                      <p className="text-sm text-content-tertiary">
                        {team.memberCount || 0} {(team.memberCount || 0) === 1 ? "member" : "members"}
                      </p>
                    </div>
                  </div>
                  {isPlatformAdmin && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedTeam(team.uuid);
                      }}
                      className="p-1.5 text-content-tertiary hover:text-content-primary hover:bg-interactive-hover rounded-lg transition-colors opacity-0 group-hover:opacity-100"
                    >
                      <MoreHorizontal className="h-4 w-4" />
                    </button>
                  )}
                </div>

                {team.description && (
                  <p className="mt-3 text-sm text-content-tertiary line-clamp-2 break-words">{team.description}</p>
                )}

                {/* Footer */}
                <div className="mt-4 flex items-center justify-between">
                  <div className="flex items-center gap-2 text-sm text-content-tertiary">
                    <Users className="h-4 w-4" />
                    <span>
                      {team.memberCount || 0} {(team.memberCount || 0) === 1 ? "member" : "members"}
                    </span>
                  </div>
                  <span className="text-xs text-brand-primary font-medium">Open Hub</span>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-16">
            <div className="h-16 w-16 rounded-2xl bg-linear-to-br from-brand-primary/20 to-brand-secondary/20 flex items-center justify-center mb-4">
              <Users className="h-8 w-8 text-brand-primary" />
            </div>
            <h3 className="text-lg font-medium text-content-primary">No teams found</h3>
            <p className="text-content-tertiary mt-1 text-center max-w-sm">
              {searchQuery
                ? "Try adjusting your search query"
                : "You are not a member of any teams yet. Ask your admin to add you to a team."}
            </p>
            {!searchQuery && isPlatformAdmin && (
              <button
                onClick={() => setShowCreateModal(true)}
                className="mt-6 flex items-center gap-2 px-4 py-2.5 bg-brand-gradient text-white rounded-lg font-medium hover:opacity-90 transition-opacity"
              >
                <Plus className="h-4 w-4" />
                Create Team
              </button>
            )}
          </div>
        )}
      </div>

      {/* Create Modal (admin only) */}
      {showCreateModal && isPlatformAdmin && (
        <TeamFormModal
          mode="create"
          busy={creating}
          onSubmit={handleCreateTeam}
          onClose={() => setShowCreateModal(false)}
        />
      )}

      {/* Team Actions Dropdown (admin only) */}
      {selectedTeam && isPlatformAdmin && (
        <div
          className="fixed inset-0 z-40"
          role="presentation" // NOSONAR — decorative backdrop element
          onClick={() => setSelectedTeam(null)}
          onKeyDown={(e) => {
            if (e.key === "Escape") setSelectedTeam(null);
          }}
        >
          <div // NOSONAR - context menu container, stopPropagation prevents backdrop dismiss
            className="absolute right-10 top-40 bg-surface-primary border border-border-primary rounded-lg shadow-xl py-1 min-w-[160px]"
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => {
                navigate(`/teams/${selectedTeam}`);
                setSelectedTeam(null);
              }}
              className="w-full flex items-center gap-3 px-4 py-2 text-sm text-content-secondary hover:bg-interactive-hover transition-colors"
            >
              <ExternalLink className="h-4 w-4" />
              Open Hub
            </button>
            <button
              onClick={() => {
                navigate(`/teams/${selectedTeam}/manage`);
                setSelectedTeam(null);
              }}
              className="w-full flex items-center gap-3 px-4 py-2 text-sm text-content-secondary hover:bg-interactive-hover transition-colors"
            >
              <UserPlus className="h-4 w-4" />
              Manage Members
            </button>
            <button
              onClick={() => {
                navigate(`/teams/${selectedTeam}/manage`);
                setSelectedTeam(null);
              }}
              className="w-full flex items-center gap-3 px-4 py-2 text-sm text-content-secondary hover:bg-interactive-hover transition-colors"
            >
              <Pencil className="h-4 w-4" />
              Edit Team
            </button>
            <div className="border-t border-border-primary my-1" />
            <button
              onClick={() => handleDeleteTeam(selectedTeam)}
              className="w-full flex items-center gap-3 px-4 py-2 text-sm text-red-400 hover:bg-interactive-hover transition-colors"
            >
              <Trash2 className="h-4 w-4" />
              Delete Team
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
